/**
 * Группы колонок (двухуровневая шапка): строка над заголовками, где подряд
 * идущие колонки одной группы получают общую ячейку с подписью.
 *
 * Группа строится по текущему порядку и видимости колонок, а не по схеме:
 * перестановка или скрытие колонки просто меняет состав ячеек. Ширина ячейки —
 * сумма треков её колонок (`grid-column: span N` в той же сетке, что и шапка).
 */

/** Колонка в порядке показа — то, что нужно для раскладки строки групп. */
export interface IColumnGroupSource {
    id: string;
    /** Подпись группы; пусто — колонка вне групп. */
    group?: string;
    /** Зона закрепления: ячейка группы не переходит её границу. */
    pinned?: 'left' | 'right';
}

/** Ячейка строки групп: подпись над колонками группы или пустое место над колонкой вне групп. */
export interface IColumnGroupBand {
    label?: string;
    columnIds: string[];
}

/** Строка групп нужна, только если хоть одна видимая колонка входит в группу. */
export const hasColumnGroups = (columns: readonly IColumnGroupSource[]): boolean => columns.some((c) => !!c.group);

/**
 * Ячейки строки групп.
 *
 * Соседние колонки объединяются, только если у них одна подпись и одна зона
 * закрепления: sticky-ячейка над закреплёнными и едущими колонками сразу
 * либо уезжала бы, либо накрывала прокручиваемые заголовки. `breakBefore` —
 * колонка, перед которой в сетке стоит распорка: ячейка не может её перешагнуть.
 */
export function computeColumnGroupBands(
    columns: readonly IColumnGroupSource[],
    breakBefore?: string | null,
): IColumnGroupBand[] {
    const bands: (IColumnGroupBand & { pinned?: 'left' | 'right' })[] = [];
    for (const column of columns) {
        const prev = bands[bands.length - 1];
        const label = column.group || undefined;
        if (prev && label && prev.label === label && prev.pinned === column.pinned && column.id !== breakBefore) {
            prev.columnIds.push(column.id);
        } else {
            bands.push({ label, pinned: column.pinned, columnIds: [column.id] });
        }
    }
    return bands.map(({ label, columnIds }) => ({ label, columnIds }));
}
